<script setup>
import { computed, defineProps, defineEmits } from 'vue'

// New-design pagination (.pagination). Works with DRF server pagination:
// totalPages from the list response, count — total record count (for the range).
const props = defineProps({
  page: { type: Number, required: true },
  totalPages: { type: Number, default: 1 },
  count: { type: Number, default: 0 },
  pageSize: { type: Number, default: 0 }, // 0 — size selector hidden
  sizes: { type: Array, default: () => [10, 25, 50] },
})

const emit = defineEmits(['update:page', 'update:pageSize'])

const pages = computed(() => Math.max(1, props.totalPages || 1))
const current = computed(() => Math.min(props.page, pages.value))

// compact page list: 1 … (cur-1, cur, cur+1) … last
const pageNums = computed(() => {
  const total = pages.value
  const cur = current.value
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const nums = [1]
  if (cur > 3) nums.push('…')
  for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) nums.push(i)
  if (cur < total - 2) nums.push('…')
  nums.push(total)
  return nums
})

const range = computed(() => {
  if (!props.count || !props.pageSize) return ''
  const start = (current.value - 1) * props.pageSize + 1
  const end = Math.min(props.count, current.value * props.pageSize)
  return `${start}–${end} of ${props.count}`
})

function go(p) {
  if (p < 1 || p > pages.value || p === current.value) return
  emit('update:page', p)
}
</script>

<template>
  <div class="pagination">
    <div v-if="pageSize" class="pagination__size">
      <span>Rows per page</span>
      <div class="pg-select">
        <select :value="pageSize" @change="emit('update:pageSize', Number($event.target.value))">
          <option v-for="s in sizes" :key="s" :value="s">{{ s }}</option>
        </select>
        <span class="mdi mdi-chevron-down caret"></span>
      </div>
    </div>
    <div v-if="range" class="pagination__range">{{ range }}</div>
    <div class="pagination__pages">
      <button class="pg-btn" type="button" :disabled="current <= 1" @click="go(current - 1)">
        <span class="mdi mdi-chevron-left"></span>
      </button>
      <template v-for="(n, i) in pageNums" :key="i">
        <span v-if="n === '…'" class="pg-btn" style="pointer-events: none; border: 0; background: none;">…</span>
        <button v-else class="pg-btn" :class="{ 'is-active': n === current }" type="button" @click="go(n)">
          {{ n }}
        </button>
      </template>
      <button class="pg-btn" type="button" :disabled="current >= pages" @click="go(current + 1)">
        <span class="mdi mdi-chevron-right"></span>
      </button>
    </div>
  </div>
</template>
